import React from 'react';
import cn from 'classnames';
import { NodeSelection } from 'prosemirror-state';

import { TFunction, useTranslation } from '@lyearn/i18n';
import { BlockMenu, Plus } from '@lyearn/icons';
import { LinkButton, Popper, Stack, Tooltip } from '@lyearn/ui';

import { TooltipTitle } from '../../components/TooltipTitle';
import { getElementPos, selectNode } from '../../utils';
import { displayReplaceMenuItem } from '../BlockMenu/utils/actions';
import { grabCursor, iconButton, zIndex15 } from './style';
import { BlockMenuButtonProps } from './types';
import {
  useAddMenuButtonClick,
  useBlockMenuButtonClick,
  useBlockMenuButtonDragStart,
  usePopperOptions,
} from './useActionBlockMenuButton';

export const BLOCK_MENU_BUTTON_POPPER_ID = 'block-menu-button-popper';

const blockButtons = (t: TFunction, isLocalizedContent: boolean) => [
  ...(isLocalizedContent
    ? []
    : [
        {
          name: 'add_menu',
          icon: Plus,
          iconButtonClassName: cn('hover:bg-transparent active:bg-transparent me-4', iconButton),
          iconClassName: 'text-icon-secondary absolute top-3 hover:text-icon-active',
          onItemClick: useAddMenuButtonClick,
          tooltipTextPrimary: t('Lygo:ClickToAddBlock'),
        },
      ]),
  {
    name: 'block_menu',
    icon: BlockMenu,
    iconClassName: 'text-icon-secondary absolute top-3 hover:text-icon-active',
    iconButtonClassName: cn(
      'rounded-4 hover:bg-transparent active:bg-transparent',
      iconButton,
      grabCursor,
    ),
    onItemClick: useBlockMenuButtonClick,
    onDragStart: useBlockMenuButtonDragStart,
    tooltipTextPrimary: t('Lygo:DragBlock'),
    tooltipTextSecondary: t('Lygo:ClickToOpenMenu'),
  },
];

export const BlockMenuButtonComponent: React.FC<BlockMenuButtonProps> = (props) => {
  let { open, anchorEl, view, isLocalizedContent } = props;

  const { t } = useTranslation();

  const popperOptions = usePopperOptions(anchorEl);

  return (
    <Popper
      anchorEl={anchorEl}
      className={cn(zIndex15, open ? 'opacity-100' : 'opacity-0')}
      id={BLOCK_MENU_BUTTON_POPPER_ID}
      open={open}
      placement="left-start"
      popperOptions={popperOptions}>
      <Stack>
        {blockButtons(t, isLocalizedContent).map((item, index) => (
          <Tooltip
            key={index}
            disableHoverListener={!anchorEl}
            placement={'bottom'}
            title={
              <TooltipTitle
                primary={item.tooltipTextPrimary}
                secondary={item.tooltipTextSecondary}
              />
            }>
            <LinkButton
              className={open ? item.iconButtonClassName : 'cursor-default'}
              data-id={item.name}
              draggable={!!item.onDragStart && !isLocalizedContent}
              variant="ghost"
              // @ts-ignore
              onClick={item.onItemClick(open, anchorEl, view)}
              // @ts-ignore
              onDragStart={item.onDragStart ? item.onDragStart(open, anchorEl, view) : undefined}>
              <item.icon className={item.iconClassName} />
            </LinkButton>
          </Tooltip>
        ))}
      </Stack>
    </Popper>
  );
};
